import { css } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { useSalesLiveCoachingConversationsQuery } from '../../../queries/AIConversation/useSalesLiveCoachingConversationsQuery';
import { Button } from '../../../web/components-v2/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { getCreatorLabel } from './creatorLabel';
import { ALL_CREATORS, type ICreatorOption, ListFilters } from './ListFilters';
import { styleSheet } from './styles';

interface IProps {
	selectedConversationId: string | null;
	onSelect: (id: string) => void;
}

/**
 * Formats a conversation's `creationDate` ISO string using the same `date-fns` helper the
 * `AIConversations` reference page (`pages/AIConversations/index.tsx`) uses, so the Coffee
 * review page renders timestamps consistently. Falls back to a placeholder for missing or
 * unparseable values.
 */
const formatConversationTimestamp = (value?: string): string => {
	if (!value) {
		return 'Unknown start time';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return 'Unknown start time';
	}

	return format(date, "MMM d, yyyy 'at' h:mm a");
};

/**
 * Mirrors the reference page's short-id derivation: the first 8 characters of the conversation
 * id, used as a label when a conversation has no title.
 */
const getShortConversationId = (id?: string): string => {
	if (!id) {
		return '';
	}

	return id.length > 8 ? id.slice(0, 8) : id;
};

/**
 * Left-panel list of an account's `SalesLiveCoaching` AI conversations, sorted newest-first.
 * Selecting a row invokes `onSelect` with the conversation id; the selected row is highlighted.
 * Loading uses the shared `LoadingSpinner`; query errors surface via the hook's `meta`
 * toast, so we render an empty body rather than a broken list when data is unavailable.
 */
export function ConversationList({ selectedConversationId, onSelect }: IProps) {
	const { data: conversations, isLoading, isError } = useSalesLiveCoachingConversationsQuery();

	// Client-side filter state. The list is account-wide and fixed to SalesLiveCoaching, so all
	// filtering happens over the already-loaded data — no extra server params are added.
	const [search, setSearch] = React.useState('');
	const [creatorId, setCreatorId] = React.useState<string>(ALL_CREATORS);
	// Inclusive date bounds as ISO `YYYY-MM-DD` strings ('' = unbounded) — the shape `DateInput` uses.
	const [startDate, setStartDate] = React.useState('');
	const [endDate, setEndDate] = React.useState('');

	// Distinct creators derived from the FULL (unfiltered) data so the dropdown options are stable
	// regardless of the other active filters.
	const creatorOptions = React.useMemo<ICreatorOption[]>(() => {
		const byId = new Map<string, ICreatorOption>();
		for (const conversation of conversations ?? []) {
			const id = conversation.creator?.id;
			if (id && !byId.has(id)) {
				byId.set(id, { id, label: getCreatorLabel(conversation.creator) });
			}
		}
		return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
	}, [conversations]);

	const filteredAndSortedConversations = React.useMemo(() => {
		if (!conversations) {
			return [];
		}

		// Normalize the date bounds to epoch millis once. End bound is widened to the end of the
		// selected day so the range is inclusive of conversations created later that day.
		const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
		const endMs = endDate ? new Date(`${endDate}T23:59:59.999`).getTime() : null;
		const normalizedSearch = search.trim().toLowerCase();

		const filtered = conversations.filter(conversation => {
			// Creator filter: keep all when the sentinel is selected, else match on creator id.
			if (creatorId !== ALL_CREATORS && conversation.creator?.id !== creatorId) {
				return false;
			}

			// Date-range filter: keep rows whose creationDate is within [start, end] inclusive,
			// ignoring a bound when it is null. Conversations with no/unparseable date are excluded
			// only when a bound is active.
			if (startMs !== null || endMs !== null) {
				const time = conversation.creationDate ? new Date(conversation.creationDate).getTime() : Number.NaN;
				if (Number.isNaN(time)) {
					return false;
				}
				if (startMs !== null && time < startMs) {
					return false;
				}
				if (endMs !== null && time > endMs) {
					return false;
				}
			}

			// Search filter: case-insensitive substring over the row's title AND creator label only.
			// NOTE: message bodies are NOT loaded by the list query, so search cannot match message
			// text — it is intentionally limited to the fields rendered in each row.
			if (normalizedSearch) {
				const title = conversation.title?.trim() ?? '';
				const creatorLabel = getCreatorLabel(conversation.creator);
				const haystack = `${title} ${creatorLabel}`.toLowerCase();
				if (!haystack.includes(normalizedSearch)) {
					return false;
				}
			}

			return true;
		});

		return filtered.sort((a, b) => {
			const aTime = a.creationDate ? new Date(a.creationDate).getTime() : 0;
			const bTime = b.creationDate ? new Date(b.creationDate).getTime() : 0;
			return bTime - aTime;
		});
	}, [conversations, creatorId, startDate, endDate, search]);

	if (isLoading) {
		return (
			<div className={css(styleSheet.listBody)}>
				<div className={css(styleSheet.spinnerState)}>
					<LoadingSpinner type='large' />
				</div>
			</div>
		);
	}

	// Errors are surfaced by the query's `meta.toastErrorMessage`; avoid rendering a broken list.
	// With no data at all (account has no coaching conversations), show the bare empty state — the
	// filter controls would be meaningless without any rows to filter.
	if (isError || (conversations?.length ?? 0) === 0) {
		return <div className={css(styleSheet.listState)}>No coaching conversations for this account</div>;
	}

	return (
		<>
			<ListFilters
				search={search}
				onSearchChange={setSearch}
				creatorId={creatorId}
				onCreatorChange={setCreatorId}
				startDate={startDate}
				onStartDateChange={setStartDate}
				endDate={endDate}
				onEndDateChange={setEndDate}
				creatorOptions={creatorOptions}
			/>
			<div className={css(styleSheet.listBody)}>
				{filteredAndSortedConversations.length === 0 ? (
					<div className={css(styleSheet.listState)}>No conversations match your filters</div>
				) : (
					filteredAndSortedConversations.map(conversation => {
						const isSelected = conversation.id === selectedConversationId;
						const title = conversation.title?.trim() || `Conversation ${getShortConversationId(conversation.id)}`;

						return (
							<Button
								key={conversation.id}
								variant='ghost'
								onClick={() => onSelect(conversation.id)}
								styles={[styleSheet.listItem, ...(isSelected ? [styleSheet.listItemSelected] : [])]}
							>
								<div className={css(styleSheet.listItemTitle)}>{title}</div>
								<div className={css(styleSheet.listItemMeta)}>
									<span>{getCreatorLabel(conversation.creator)}</span>
									<span>{formatConversationTimestamp(conversation.creationDate)}</span>
								</div>
							</Button>
						);
					})
				)}
			</div>
		</>
	);
}
